import clsx from 'clsx';
import {
  forwardRef,
  type InputHTMLAttributes,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from 'react';

import styles from './Field.module.css';

interface Variant {
  readonly inline?: boolean;
  readonly invalid?: boolean;
}

const controlClass = ({ inline, invalid }: Variant, className?: string) =>
  clsx(inline ? styles.inline : styles.control, invalid && styles.invalid, className);

export const TextInput = forwardRef<
  HTMLInputElement,
  InputHTMLAttributes<HTMLInputElement> & Variant
>(function TextInput({ inline, invalid, className, ...rest }, ref) {
  return (
    <input
      ref={ref}
      aria-invalid={invalid ?? undefined}
      className={controlClass({ inline, invalid }, className)}
      {...rest}
    />
  );
});

export const TextArea = forwardRef<
  HTMLTextAreaElement,
  TextareaHTMLAttributes<HTMLTextAreaElement> & Variant
>(function TextArea({ inline, invalid, className, ...rest }, ref) {
  return (
    <textarea
      ref={ref}
      aria-invalid={invalid ?? undefined}
      className={controlClass({ inline, invalid }, className)}
      {...rest}
    />
  );
});

export interface SelectOption {
  readonly value: string;
  readonly label: string;
  readonly disabled?: boolean;
}

export interface SelectProps
  extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'children'>, Variant {
  readonly options: readonly SelectOption[];
  /** Rendered as a disabled leading option, e.g. "— Select —". */
  readonly placeholder?: string;
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  { options, placeholder, inline, invalid, className, ...rest },
  ref,
) {
  return (
    <select
      ref={ref}
      aria-invalid={invalid ?? undefined}
      className={controlClass({ inline, invalid }, className)}
      {...rest}
    >
      {placeholder !== undefined && <option value="">{placeholder}</option>}
      {options.map((option) => (
        <option key={option.value} value={option.value} disabled={option.disabled}>
          {option.label}
        </option>
      ))}
    </select>
  );
});
